import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  guardarProgreso,
  obtenerCapitulo,
  obtenerCapitulos,
  obtenerPaginas,
} from '../api/consultas'
import {
  clavePagina,
  estadosMejora,
  rutaMejorada,
  rutaOriginal,
  usarMejora,
} from '../api/mejora'
import { esPareja, olvidarCosturas } from '../lib/costuras'
import {
  alternarDoble,
  anteriorUnidad,
  apuntarAutomatica,
  esDoble,
  inicioUnidad,
  leerDobles,
  ordenDe,
  siguienteUnidad,
  type Dobles,
} from '../lib/dobles'
import { detectarModo, guardarFormato, hayQueMedir, leerFormato, type Modo } from '../lib/formato'
import { precargar, useImagen } from '../lib/imagenes'
import {
  apuntarProgresoLocal,
  dejarPendiente,
  descargarCapitulo,
  fichaComoCapitulo,
  fichaDescargada,
  hermanosDescargados,
} from '../lib/offline'
import {
  guardarPosicion,
  leerPosicion,
  olvidarPosicion,
  type Posicion,
} from '../lib/posicion'
import { traducirPunto, type Traduccion } from '../api/traducir'
import { pareceTruncado, repararCapitulo } from '../lib/truncados'
import { useZoomPagina, useZoomTira } from '../lib/zoom'

type Sentido = 'ltr' | 'rtl'

/** Lo que ensena el panel de traduccion mientras va y cuando vuelve. */
type Peticion =
  | { estado: 'pensando' }
  | ({ estado: 'listo' } & Traduccion)
  | { estado: 'error'; mensaje: string }

const CLAVE_SENTIDO = 'yomou.lector.sentido'
/**
 * Parte del ancho que ocupa cada franja de pasar pagina, a los lados.
 *
 * Estuvo en 0.35 y se bajo: con las franjas asi de anchas, el centro se
 * quedaba en un 30% de la pantalla y apuntar a el para sacar los controles
 * pasaba de pagina sin querer bastante a menudo. Con 0.28 el centro es casi
 * la mitad del ancho —unos 172 px en un iPhone contra 117 antes— y las
 * franjas siguen siendo comodas de alcanzar con el pulgar.
 */
const ZONA_PASAR = 0.28
const PAGINAS_POR_DELANTE = 3

function Pagina({
  ruta,
  numero,
  prioritario = false,
  alMedir,
}: {
  ruta: string
  numero: number
  prioritario?: boolean
  /** Tamano real del fichero, para saber si la pagina es una doble entera. */
  alMedir?: (ancho: number, alto: number) => void
}) {
  const { url, estado, reintentar } = useImagen(ruta, { prioritario })

  if (estado === 'fallo') {
    return (
      <div className="pagina-fallo">
        <p>No se pudo cargar la pagina {numero}</p>
        <button
          className="boton secundario"
          onClick={(e) => {
            // Sin esto el toque cuenta tambien como pasar de pagina
            e.stopPropagation()
            reintentar()
          }}
        >
          Reintentar
        </button>
      </div>
    )
  }
  if (!url) {
    return <div className="pagina-cargando">{numero}</div>
  }

  // Mientras llega la nueva se mantiene la anterior en pantalla, pero con un
  // indicador: si no, pasar de pagina parece que no ha hecho nada.
  return (
    <>
      {/* El indice viaja en el DOM porque el lector solo tiene el <img> que se
          toco: de ahi saca que pagina es y en que punto de ella cayo el dedo. */}
      <img
        src={url}
        alt={`Pagina ${numero}`}
        className="pagina"
        data-indice={numero - 1}
        onLoad={(e) => alMedir?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
      />
      {estado === 'cargando' && <span className="pagina-espera" />}
    </>
  )
}

export default function Lector() {
  const { id = '' } = useParams()
  const capituloId = Number(id)
  const navegar = useNavigate()
  const clienteConsultas = useQueryClient()

  const [sentido, setSentido] = useState<Sentido>(
    () => (localStorage.getItem(CLAVE_SENTIDO) as Sentido) ?? 'rtl',
  )
  const [modo, setModo] = useState<Modo>('paginado')
  const [indice, setIndice] = useState(0)
  const [terminado, setTerminado] = useState(false)
  // Aviso antes de saltar al capitulo anterior. Ver `responderAnterior`.
  const [preguntaAnterior, setPreguntaAnterior] = useState(false)
  const [verControles, setVerControles] = useState(true)
  const [rescatando, setRescatando] = useState(false)
  // Modo traducir: los toques dejan de pasar pagina y pasan a leer el texto
  // que hay debajo del dedo. Ver `traducirToque`.
  const [traduciendo, setTraduciendo] = useState(false)
  const [traduccion, setTraduccion] = useState<Peticion | null>(null)
  // Pagina a la que hay que volver al abrir, mientras se llega a ella. En
  // paginado se resuelve en un render; en tira vertical hay que desplazarse,
  // y hasta que se llega no se sabe por donde va la lectura de verdad.
  const [porRestaurar, setPorRestaurar] = useState<Posicion | null>(null)
  // Sube al rescatar un capitulo: fuerza a remontar las paginas ya pintadas
  const [revision, setRevision] = useState(0)
  // Medias paginas unidas a mano en este capitulo. Ver `lib/dobles`.
  const [dobles, setDobles] = useState<Dobles>(() => leerDobles(capituloId))
  // Tamano del fichero de la pagina que se esta viendo: una pagina mas ancha
  // que alta es una doble que la fuente manda entera, y se pinta igual que
  // las dos mitades unidas.
  const [natural, setNatural] = useState<{ ancho: number; alto: number } | null>(null)
  const rescatados = useRef(new Set<number>())
  const contenedorRef = useRef<HTMLDivElement>(null)
  // El envoltorio que se amplia en paginado, y los dos elementos que reciben
  // los toques. Estos ultimos van en estado y no en referencia porque el
  // lector se pinta antes con la pantalla de "Buscando las paginas": si el
  // enganche de los gestos mirara una referencia no se enteraria de que el
  // lienzo ha aparecido despues.
  const zoomRef = useRef<HTMLDivElement>(null)
  const [lienzo, setLienzo] = useState<HTMLDivElement | null>(null)
  const [tira, setTira] = useState<HTMLDivElement | null>(null)
  // Numero de la ultima traduccion pedida: si se tocan dos bocadillos seguidos,
  // la respuesta de la primera ya no interesa y no debe pisar a la segunda.
  const peticionTraduccion = useRef(0)
  // Ultimo capitulo al que se le devolvio la tira a lo alto (ver mas abajo)
  const tiraColocada = useRef<number | null>(null)

  // Si el capitulo esta descargado en el telefono se usa lo local: funciona
  // sin conexion y ademas evita una ida y vuelta al servidor.
  const { data: capitulo } = useQuery({
    queryKey: ['capitulo', capituloId],
    queryFn: async () => {
      try {
        return await obtenerCapitulo(capituloId)
      } catch (err) {
        const ficha = await fichaDescargada(capituloId)
        if (ficha) return fichaComoCapitulo(ficha)
        throw err
      }
    },
    enabled: Number.isFinite(capituloId),
  })

  const { data: originales, isLoading, error } = useQuery({
    queryKey: ['paginas', capituloId],
    queryFn: async () => {
      const ficha = await fichaDescargada(capituloId)
      if (ficha) return ficha.paginas
      return obtenerPaginas(capituloId)
    },
    enabled: Number.isFinite(capituloId),
    staleTime: Infinity,
  })

  /*
   * Paginas mejoradas (ver `api/mejora`).
   *
   * Una pagina mejorada es **otra ruta**, y el lector entero trabaja con
   * rutas: la cache, el almacen, las descargas, el reconocedor de dobles. Asi
   * que basta con cambiar la lista aqui y todo lo de abajo se mejora solo,
   * sin tocar ni una linea mas.
   *
   * Si el capitulo no esta mejorado, o el servicio no contesta, se leen las de
   * siempre: esto nunca puede dejarte sin capitulo.
   */
  const clave = originales?.length ? clavePagina(originales[0]!) : null
  const { data: mejora } = useQuery({
    queryKey: ['mejora', clave?.manga, clave?.capitulo],
    queryFn: async () => {
      const estados = await estadosMejora(clave!.manga, [clave!.capitulo])
      return estados[clave!.capitulo]?.estado ?? 'nada'
    },
    enabled: Boolean(clave) && usarMejora(),
    staleTime: 60_000,
    retry: false,
  })

  const paginas = useMemo(() => {
    if (!originales || mejora !== 'lista') return originales
    return originales.map((ruta) => rutaMejorada(ruta))
  }, [originales, mejora])

  // Hermanos, para saltar al capitulo siguiente al llegar al final. Sin
  // conexion solo se puede saltar entre los que estan descargados.
  const { data: hermanos } = useQuery({
    queryKey: ['capitulos', capitulo?.mangaId],
    queryFn: async () => {
      try {
        return await obtenerCapitulos(capitulo!.mangaId)
      } catch (err) {
        const locales = await hermanosDescargados(capitulo!.mangaId)
        if (locales.length) return locales.map(fichaComoCapitulo)
        throw err
      }
    },
    enabled: Boolean(capitulo?.mangaId),
  })

  const { anterior, siguiente } = useMemo(() => {
    if (!hermanos || !capitulo) return { anterior: undefined, siguiente: undefined }
    const i = hermanos.findIndex((c) => c.id === capitulo.id)
    return { anterior: hermanos[i - 1], siguiente: hermanos[i + 1] }
  }, [hermanos, capitulo])

  /*
   * Paginas dobles.
   *
   * Hay dos formas de que aparezca una y las dos acaban igual: puesta a alto
   * completo y recorriendola de lado, porque encajada a lo ancho se queda en
   * una tira de la mitad de alto. Medido con el CSS de verdad a tamano de
   * iPhone: una pagina normal sale a 746 px de alto y una doble entera a 373.
   *
   * - La fuente la manda **entera** y mide mas ancho que alto (Black Clover
   *   hasta el capitulo 380: paginas de 1200x1800 y dobles de 2133x1600).
   * - La fuente la manda **partida** y el usuario ha unido las dos mitades a
   *   mano (del 381 en adelante, ver `lib/dobles`).
   */
  const esPar = esDoble(indice, dobles) && indice + 1 < (paginas?.length ?? 0)
  const ancha = esPar || (natural !== null && natural.ancho > natural.alto)
  const medirPagina = useCallback((ancho: number, alto: number) => {
    setNatural({ ancho, alto })
  }, [])

  /*
   * Al rescatar un capitulo descargado a medias las paginas cambian sin
   * cambiar de ruta, asi que lo medido de ellas ya no vale.
   */
  useEffect(() => {
    if (paginas?.length) olvidarCosturas(capituloId, paginas)
  }, [revision, capituloId, paginas])

  /*
   * Reconocer las dobles: la de aqui y las que vienen.
   *
   * Lo decide `lib/costuras` mirando si el dibujo continua en el corte. Se
   * miran tambien las dos paginas siguientes, y esto es lo importante: si solo
   * se decidiera la de aqui, con pasar de pagina antes de que conteste ya te
   * habias saltado la doble —te quedabas en su mitad izquierda, y estando ahi
   * ya nadie vuelve a preguntar por la pareja—. Decidiendo por delante, la
   * doble esta reconocida antes de que llegues a ella.
   *
   * Se pregunta de una en una y no todas a la vez: cada pagina que se mide son
   * unos milisegundos de recorrer pixeles, y encadenadas no dan tirones.
   *
   * Lo que el usuario une o separa a mano manda: `apuntarAutomatica` no toca
   * ninguna de las dos cosas.
   */
  useEffect(() => {
    if (modo !== 'paginado' || !paginas?.length) return
    let vigente = true
    let espera: ReturnType<typeof setTimeout> | undefined

    const mirar = async (quedan: number) => {
      let dudosa = false
      for (const n of [indice, indice + 1, indice + 2]) {
        if (!vigente) return
        if (n < 0 || n + 1 >= paginas.length) continue
        if (dobles.unidas.has(n) || dobles.sueltas.has(n)) continue
        const pareja = await esPareja(capituloId, paginas, n)
        if (!vigente) return
        // No se ha podido conseguir alguna pagina: se vuelve a preguntar
        if (pareja === null) dudosa = true
        // El automatico solo reconoce la colocacion normal del manga
        else if (pareja) setDobles((previas) => apuntarAutomatica(capituloId, n, previas, 'rtl'))
      }
      if (dudosa && quedan > 0) espera = setTimeout(() => void mirar(quedan - 1), 1500)
    }
    void mirar(4)

    return () => {
      vigente = false
      if (espera) clearTimeout(espera)
    }
  }, [capituloId, paginas, indice, modo, dobles])


  /*
   * Rescate de capitulos que se descargaron a medias.
   *
   * Suwayomi sirve los capitulos descargados desde su disco y no vuelve a
   * mirar la fuente nunca, asi que si la auto-descarga salto mientras la web
   * subia el capitulo por tandas, se quedo con las paginas que hubiera y ahi
   * se quedaria para siempre. Se compara con lo que mide un capitulo normal
   * de la serie y, si canta, se borra la descarga y se vuelve a preguntar.
   *
   * Se hace aqui y no al abrir la ficha porque hasta que no estan las paginas
   * no se sabe cuantas hay, y porque asi solo se gasta la peticion —cara en
   * fuentes como LeerCapitulo— en el capitulo que se va a leer de verdad.
   */
  useEffect(() => {
    if (!capitulo || capitulo.id !== capituloId) return
    if (!hermanos || !paginas?.length) return
    if (rescatados.current.has(capituloId)) return
    if (!pareceTruncado(capitulo, paginas.length, hermanos)) return

    const viejas = paginas
    rescatados.current.add(capituloId)
    setRescatando(true)

    void repararCapitulo(capituloId, viejas)
      .then(async (nuevas) => {
        if (nuevas.length <= viejas.length) return
        clienteConsultas.setQueryData(['paginas', capituloId], nuevas)
        setRevision((n) => n + 1)

        // La copia del telefono, si la hay, arrastra las mismas paginas de
        // menos. Se refresca por detras: las paginas ya estan en pantalla y
        // no hay por que hacer esperar a que se copien otra vez.
        void fichaDescargada(capituloId).then((ficha) => {
          if (ficha) return descargarCapitulo(capituloId)
        })

        await Promise.all([
          clienteConsultas.invalidateQueries({ queryKey: ['capitulo', capituloId] }),
          clienteConsultas.invalidateQueries({ queryKey: ['capitulos', capitulo.mangaId] }),
        ])
      })
      .catch(() => {
        // Sin red o con la fuente caida no hay nada que rescatar: se lee lo
        // que hay y se vuelve a intentar la proxima vez que se abra.
        rescatados.current.delete(capituloId)
      })
      .finally(() => setRescatando(false))
  }, [capitulo, hermanos, paginas, capituloId, clienteConsultas])

  /*
   * Modo de lectura. Los manhwa son una tira vertical continua y se leen
   * desplazando hacia abajo; el manga se lee pasando paginas. Se aplica lo
   * guardado de esta serie al instante y, si nunca se eligio a mano, se
   * mide el capitulo para decidirlo solo.
   */
  useEffect(() => {
    const mangaId = capitulo?.mangaId
    if (!mangaId) return

    const guardado = leerFormato(mangaId)
    if (guardado) setModo(guardado.modo)
    if (!hayQueMedir(guardado) || !paginas?.length) return

    let cancelado = false
    void detectarModo(paginas).then((detectado) => {
      if (cancelado || !detectado) return
      setModo(detectado)
      guardarFormato(mangaId, detectado, false)
    })
    return () => {
      cancelado = true
    }
  }, [capitulo?.mangaId, paginas])

  /*
   * Al pasar al capitulo siguiente cambia la ruta pero el componente no se
   * desmonta, asi que el indice de pagina sobrevivia del capitulo anterior:
   * terminabas en la 74 y el siguiente abria en la 74. Hay que reiniciarlo
   * a mano en cuanto cambia el id.
   */
  useEffect(() => {
    setIndice(0)
    setTerminado(false)
    setPreguntaAnterior(false)
    setVerControles(true)
    setPorRestaurar(null)
    setTraduccion(null)
    setDobles(leerDobles(capituloId))
  }, [capituloId])

  // El tamano es el de la pagina que se ve, asi que se olvida al pasar
  useEffect(() => {
    setNatural(null)
  }, [indice, capituloId, revision])

  /*
   * ...y en tira vertical hay que devolverla ademas a lo alto a mano.
   *
   * El que desplaza es `.lector-webtoon`, y ese div tampoco se desmonta al
   * saltar de capitulo: solo le cambian los hijos, asi que conserva el
   * `scrollTop` del final del capitulo anterior. Al pintar el nuevo —todavia
   * sin imagenes, solo los huecos de 60vh— el navegador recorta ese scroll a
   * lo que mida la tira de huecos, y ahi se queda mientras las imagenes van
   * llegando y la tira crece: el capitulo abria un poco avanzado. Medido en
   * el banco con 12 paginas de 2.000 px y ventana de 844: abria a 2.926 px,
   * o sea a mitad de la pagina 2, y con este arreglo a 0.
   *
   * Solo se nota al encadenar capitulos: la lista de paginas del siguiente se
   * adelanta a mitad del capitulo (mas abajo), y con ella ya en cache no hay
   * pantalla de "Buscando las paginas" que desmonte la tira y la deje limpia.
   * Entrando desde la ficha si la hay, y por eso alli abria bien.
   *
   * Va en `useLayoutEffect` para colocarla antes de pintar, que si no se ve
   * el salto. Y la guardia por capitulo importa: este efecto se repite cuando
   * cambian las paginas —al rescatar un capitulo descargado a medias— y sin
   * ella le robaria el scroll al usuario en mitad de la lectura.
   */
  useLayoutEffect(() => {
    if (modo !== 'webtoon' || !paginas?.length) return
    if (tiraColocada.current === capituloId) return
    const caja = contenedorRef.current?.parentElement
    if (!caja) return
    caja.scrollTop = 0
    tiraColocada.current = capituloId
  }, [capituloId, modo, paginas])

  // Retomar donde se dejo la lectura. Se comprueba que los datos sean ya
  // los del capitulo actual y no los del anterior, que siguen en cache.
  useEffect(() => {
    if (capitulo?.id !== capituloId) return
    if (capitulo.isRead) return

    // La local sabe cuanto llevabas DENTRO de la pagina; la del servidor solo
    // el numero de pagina, pero puede ir por delante si leiste en otro sitio.
    const local = leerPosicion(capituloId)
    const delServidor = capitulo.lastPageRead
    let posicion: Posicion | null = null
    if (local && local.indice >= delServidor) posicion = local
    else if (delServidor > 0) posicion = { indice: delServidor, desplazamiento: 0 }
    if (!posicion) return

    setIndice(inicioUnidad(posicion.indice, leerDobles(capituloId)))
    setPorRestaurar(posicion)
  }, [capitulo, capituloId])

  /*
   * Retomar la lectura en tira vertical.
   *
   * En paginado basta con el indice, porque solo se pinta esa pagina. Aqui
   * estan todas y hay que desplazarse hasta la que toca, con una pega: las
   * imagenes se cargan segun se acercan y hasta entonces ocupan el hueco de
   * relleno, que es mucho mas bajo que una tira de manhwa de verdad. Segun
   * van llegando las de arriba, todo lo de abajo se desplaza. Por eso no
   * basta con un scroll y ya: hay que reponerlo cada vez que cambia el alto,
   * y soltarlo en cuanto el usuario toca la pantalla, o le estariamos
   * peleando el desplazamiento.
   */
  useEffect(() => {
    if (modo !== 'webtoon' || porRestaurar === null) return
    if (!paginas?.length) return
    const contenedor = contenedorRef.current
    if (!contenedor) return

    const caja = contenedor.parentElement
    if (!caja) return

    let vivo = true
    let cuadro = 0
    let ultimo = -1
    let quietoDesde = 0

    /*
     * A que altura del scroll hay que estar. Es invariante al propio scroll
     * —si te mueves, el rectangulo se mueve igual y en sentido contrario—,
     * asi que solo cambia cuando algo de arriba crece.
     */
    function objetivo(): number | null {
      const hijos = contenedor!.children
      const hijo = hijos[Math.min(porRestaurar!.indice, hijos.length - 1)] as
        | HTMLElement
        | undefined
      if (!hijo) return null
      const arriba = hijo.getBoundingClientRect().top - caja!.getBoundingClientRect().top
      return caja!.scrollTop + arriba + porRestaurar!.desplazamiento
    }

    function soltar() {
      vivo = false
      setPorRestaurar(null)
    }

    /*
     * Un solo scrollIntoView no basta: las paginas de arriba siguen llegando
     * despues y empujan el destino hacia abajo. Chrome lo disimula con su
     * anclaje de scroll, pero **Safari no tiene anclaje** y el iPhone es
     * Safari. Medido en un banco de pruebas con el anclaje desactivado, 40
     * paginas y destino en la 10: sin reponer acaba a 18.109 px de donde
     * tocaba; con ResizeObserver, 0 px (42 reposiciones); asi, 0 px (12).
     * Se eligio el fotograma porque se suelta solo cuando la tira lleva
     * 600 ms quieta, en vez de esperar al techo de 8 s.
     */
    function paso(ahora: number) {
      if (!vivo) return
      const destino = objetivo()
      if (destino !== null) {
        // No basta con que el destino deje de moverse: hay que comprobar que
        // se ha LLEGADO. Mientras las paginas de abajo no han cargado, la
        // tira mide menos de lo que medira y el navegador recorta el scroll;
        // el destino calculado no cambia, asi que sin esta comprobacion el
        // bucle se daba por satisfecho estando aun a 613 px (medido).
        const llegado = Math.abs(caja!.scrollTop - destino) <= 1
        if (!llegado || Math.abs(destino - ultimo) > 1) {
          ultimo = destino
          quietoDesde = ahora
          caja!.scrollTop = destino
        } else if (ahora - quietoDesde > 600) {
          soltar()
          return
        }
      }
      cuadro = requestAnimationFrame(paso)
    }

    cuadro = requestAnimationFrame(paso)
    // Techo por si la tira no se asienta nunca y el usuario no toca nada
    const temporizador = setTimeout(soltar, 8000)
    window.addEventListener('touchstart', soltar, { passive: true })
    window.addEventListener('wheel', soltar, { passive: true })

    // Ojo: aqui solo se desmonta lo que se monto. Olvidar la pagina destino
    // seria un fallo: este efecto se relanza cuando llegan las paginas o se
    // decide el modo, y entonces se cancelaria la restauracion a medias.
    return () => {
      vivo = false
      cancelAnimationFrame(cuadro)
      clearTimeout(temporizador)
      window.removeEventListener('touchstart', soltar)
      window.removeEventListener('wheel', soltar)
    }
  }, [modo, paginas, porRestaurar])

  useEffect(() => {
    localStorage.setItem(CLAVE_SENTIDO, sentido)
  }, [sentido])

  // Guardar progreso sin castigar al servidor con una peticion por pagina
  useEffect(() => {
    if (!paginas?.length) return
    // Mientras se vuelve a la pagina de antes, el indice todavia no dice por
    // donde va la lectura: guardarlo pisaria el progreso bueno. Solo afecta a
    // la tira vertical; en paginado el indice ya es la pagina que se ve, y
    // frenar aqui dejaria la serie sin guardar progreso nunca.
    if (modo === 'webtoon' && porRestaurar !== null) return
    const esUltima = indice >= paginas.length - 1
    const temporizador = setTimeout(() => {
      apuntarProgresoLocal(capituloId, indice)
      void guardarProgreso(capituloId, indice, esUltima || undefined).catch(() => {
        // Sin red se apunta para enviarlo cuando vuelva, y la lectura sigue
        dejarPendiente(capituloId, indice, esUltima)
      })
    }, 1200)
    return () => clearTimeout(temporizador)
  }, [indice, capituloId, paginas, modo, porRestaurar])

  /*
   * Precarga de las paginas de alrededor, incluida la anterior porque
   * retroceder es tan comun como avanzar.
   *
   * Se espera a que el usuario deje de pasar paginas. Si se lanzara en cada
   * cambio, pasar deprisa por diez paginas encolaria cuarenta descargas que
   * ya no interesan, y la pagina que se esta mirando tendria que esperar a
   * que se vaciaran.
   */
  useEffect(() => {
    if (!paginas) return
    let vigente = true

    const temporizador = setTimeout(() => {
      void precargar(
        [
          ...paginas.slice(indice + 1, indice + 1 + PAGINAS_POR_DELANTE),
          ...paginas.slice(Math.max(0, indice - 1), indice),
        ],
        () => vigente,
      )
    }, 350)

    return () => {
      vigente = false
      clearTimeout(temporizador)
    }
  }, [indice, paginas])

  /*
   * Adelanta la lista de paginas del capitulo siguiente.
   *
   * Resolverla no es gratis en todas las fuentes: LeerCapitulo va a la web
   * cada vez y tarda entre diez y quince segundos, sin cachear nada. Leyendo
   * una serie larga del tiron, eso era una pantalla de "Cargando capitulo"
   * en cada salto. Pidiendola mientras se lee, el salto sale instantaneo.
   *
   * Se espera a la mitad del capitulo: da tiempo de sobra a que llegue y no
   * se gasta una peticion cara si el lector se abandona en las primeras
   * paginas. Si ya esta en cache, prefetchQuery no vuelve a pedirla.
   */
  useEffect(() => {
    if (!siguiente || !paginas?.length) return
    if (indice < Math.floor(paginas.length / 2)) return

    const id = siguiente.id
    void clienteConsultas.prefetchQuery({
      queryKey: ['paginas', id],
      queryFn: async () => {
        const ficha = await fichaDescargada(id)
        if (ficha) return ficha.paginas
        return obtenerPaginas(id)
      },
      staleTime: Infinity,
    })
  }, [siguiente, paginas, indice, clienteConsultas])

  /**
   * Marca el capitulo como leido de forma inmediata.
   *
   * El guardado periodico lleva 1.2 s de retardo para no bombardear el
   * servidor, pero al terminar un capitulo eso no vale: si pasas de la
   * ultima pagina antes de que dispare, el capitulo se queda sin marcar.
   */
  const terminar = useCallback(async () => {
    if (!paginas?.length) return
    const ultima = paginas.length - 1
    // Terminado no hay nada que retomar, y dejarla haria que reabrirlo cayera
    // a media tira en vez de al principio.
    olvidarPosicion(capituloId)
    apuntarProgresoLocal(capituloId, ultima)
    try {
      await guardarProgreso(capituloId, ultima, true)
      await Promise.all([
        clienteConsultas.invalidateQueries({ queryKey: ['capitulos', capitulo?.mangaId] }),
        clienteConsultas.invalidateQueries({ queryKey: ['biblioteca'] }),
      ])
    } catch {
      // Sin red queda apuntado y se envia al reconectar
      dejarPendiente(capituloId, ultima, true)
    }
  }, [capituloId, paginas, capitulo?.mangaId, clienteConsultas])

  /**
   * Vuelve a la ficha releyendo el progreso, que acaba de cambiar.
   *
   * Saltar de capitulo **reemplaza** la entrada del historial en vez de
   * apilarla. Si no, leyendo del tiron se acumulaba una entrada por capitulo
   * y este boton te devolvia al capitulo anterior en lugar de sacarte: por
   * eso funcionaba al entrar desde la ficha y no al encadenar.
   *
   * Aun asi puede no haber historial —abrir un capitulo desde un aviso push
   * entra directo en `/leer/:id`—, y ahi `atras` te sacaria de la app. React
   * Router numera sus entradas en `history.state.idx`; si estamos en la
   * primera, se va a la ficha a mano.
   */
  const volver = useCallback(() => {
    void clienteConsultas.invalidateQueries({ queryKey: ['capitulos', capitulo?.mangaId] })
    // Y la biblioteca, que se ordena por lo ultimo leido: sin esto la serie
    // que acabas de leer no subia al primer puesto hasta pasados 5 minutos,
    // que es lo que React Query da por buena la respuesta anterior.
    void clienteConsultas.invalidateQueries({ queryKey: ['biblioteca'] })
    const indiceHistorial = (window.history.state as { idx?: number } | null)?.idx
    if (typeof indiceHistorial === 'number' && indiceHistorial > 0) {
      navegar(-1)
      return
    }
    if (capitulo?.mangaId) navegar(`/manga/${capitulo.mangaId}`, { replace: true })
    else navegar('/')
  }, [clienteConsultas, capitulo?.mangaId, navegar])

  /**
   * Contesta al aviso de "capitulo anterior".
   *
   * Retroceder desde la primera pagina se iba al capitulo anterior en el
   * acto, y la primera pagina es justo donde mas facil es pasarse: un toque
   * o una flecha de mas y estabas fuera del capitulo. Ahora el primer
   * retroceso solo saca el aviso; hay que repetirlo para irse, y avanzar lo
   * quita y te deja en la misma pagina. Es la pareja de la pantalla de
   * "Capitulo terminado", que ya hacia lo mismo por el otro lado.
   */
  const responderAnterior = useCallback(
    (confirmar: boolean) => {
      setPreguntaAnterior(false)
      if (confirmar && anterior) navegar(`/leer/${anterior.id}`, { replace: true })
    },
    [anterior, navegar],
  )

  const irA = useCallback(
    (nuevo: number) => {
      if (!paginas) return
      if (nuevo < 0) {
        // Sin capitulo anterior no hay nada que preguntar: se queda quieto
        if (anterior) setPreguntaAnterior(true)
        return
      }
      if (nuevo >= paginas.length) {
        // No se salta solo al siguiente: se avisa de que termino y se deja
        // decidir. Saltar de golpe desorienta, sobre todo si te pasaste de
        // pagina sin querer.
        void terminar()
        setTerminado(true)
        return
      }
      // Si cae en la segunda mitad de una doble —la barra de abajo, el
      // progreso del servidor— se sube a la primera, que es la que se pinta
      setIndice(inicioUnidad(nuevo, dobles))
    },
    [paginas, anterior, terminar, dobles],
  )

  // Teclado, util al probar en el navegador del PC
  useEffect(() => {
    // Cual de las dos flechas retrocede depende del sentido de lectura
    const atras = sentido === 'rtl' ? 'ArrowRight' : 'ArrowLeft'
    const alante = sentido === 'rtl' ? 'ArrowLeft' : 'ArrowRight'

    function alPulsar(e: KeyboardEvent) {
      if (e.key === 'Escape' && traduciendo) {
        setTraduciendo(false)
        setTraduccion(null)
        return
      }
      // Con el aviso puesto las flechas lo contestan en vez de pasar pagina:
      // repetir la de retroceder se va al capitulo anterior, la otra —o
      // Escape— lo quita sin moverse de donde estabas.
      if (preguntaAnterior) {
        if (e.key === atras) responderAnterior(true)
        else if (e.key === alante || e.key === 'Escape') responderAnterior(false)
        return
      }
      if (modo === 'webtoon') return
      // Una doble se pasa de una vez: sus dos mitades son una sola pagina
      const adelante = siguienteUnidad(indice, dobles)
      const detras = anteriorUnidad(indice, dobles)
      if (e.key === 'ArrowRight') irA(sentido === 'rtl' ? detras : adelante)
      if (e.key === 'ArrowLeft') irA(sentido === 'rtl' ? adelante : detras)
      if (e.key === 'Escape') volver()
    }
    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [
    indice,
    irA,
    sentido,
    modo,
    volver,
    traduciendo,
    preguntaAnterior,
    responderAnterior,
    dobles,
  ])

  /*
   * Zoom con los dedos.
   *
   * Hay uno por modo y no son el mismo mecanismo: la tira vertical no admite
   * un `transform` sin romperle las medidas al guardado de posicion y al
   * observador de pagina. Esta contado en `lib/zoom.ts`; aqui solo se decide
   * cual esta vivo y cuando se quita.
   */
  const zoomPagina = useZoomPagina(lienzo, zoomRef, modo === 'paginado')
  const zoomTira = useZoomTira(tira, contenedorRef, modo === 'webtoon')
  const zoom = modo === 'paginado' ? zoomPagina : zoomTira
  const escalaTira = zoomTira.escala

  /*
   * Al cambiar de pagina, de capitulo o de modo se vuelve al tamano normal.
   * El zoom es para mirar una vineta de cerca, no un ajuste que se arrastre:
   * dejarlo puesto haria que la pagina siguiente abriera por una esquina.
   */
  const quitarZoomPagina = zoomPagina.quitar
  const quitarZoomTira = zoomTira.quitar
  useEffect(() => {
    quitarZoomPagina()
  }, [indice, capituloId, modo, quitarZoomPagina])
  useEffect(() => {
    quitarZoomTira()
  }, [capituloId, modo, quitarZoomTira])

  /*
   * Una doble se abre pegada al borde por el que empieza la lectura: a la
   * derecha leyendo de derecha a izquierda, que es como se lee este manga.
   *
   * A alto completo no cabe a lo ancho, y centrada deja fuera de la pantalla
   * justo la vineta por la que se empieza.
   *
   * Va en un fotograma aparte porque en el del cambio de pagina la imagen
   * todavia no esta colocada, y `natural` esta en las dependencias para que
   * se repita en cuanto llega: antes de eso no hay nada que mover.
   */
  const colocarZoom = zoomPagina.colocar
  useEffect(() => {
    if (modo !== 'paginado' || !ancha) return
    const empieza = esPar ? ordenDe(indice, dobles) : sentido
    const fotograma = requestAnimationFrame(() => colocarZoom(empieza === 'rtl' ? 'der' : 'izq'))
    return () => cancelAnimationFrame(fotograma)
  }, [ancha, esPar, dobles, indice, natural, modo, sentido, colocarZoom])

  /**
   * Traduce el texto que hay donde se toco.
   *
   * Del toque solo se manda la pagina y el punto, en tanto por uno sobre la
   * imagen: la app no sabe —ni le hace falta— a que tamano la esta pintando el
   * movil, y asi el servidor puede recortar el bocadillo sobre el original.
   *
   * El OCR y la traduccion los hace el VPS con software libre, asi que tocar
   * bocadillos no cuesta dinero por mucho que se use.
   */
  function traducirToque(e: React.MouseEvent<HTMLDivElement>) {
    const tocado = e.target as HTMLElement
    if (!(tocado instanceof HTMLImageElement) || !paginas) {
      // Se toco un hueco de pagina sin cargar, o el margen negro de los lados
      setTraduccion({ estado: 'error', mensaje: 'Toca encima de la pagina' })
      return
    }
    const dePagina = Number(tocado.dataset.indice)
    const ruta = paginas[Number.isFinite(dePagina) ? dePagina : indice]
    if (!ruta) return
    // Al servicio de traducir se le manda siempre la de Suwayomi: el recorte
    // lo hace sobre el original y no sabe nada de las mejoradas.
    const rutaParaTraducir = rutaOriginal(ruta)

    const caja = tocado.getBoundingClientRect()
    const x = (e.clientX - caja.left) / caja.width
    const y = (e.clientY - caja.top) / caja.height

    const peticion = peticionTraduccion.current + 1
    peticionTraduccion.current = peticion
    setTraduccion({ estado: 'pensando' })
    void traducirPunto(rutaParaTraducir, x, y)
      .then((datos) => {
        if (peticion !== peticionTraduccion.current) return
        setTraduccion({ estado: 'listo', ...datos })
      })
      .catch((error: Error) => {
        if (peticion !== peticionTraduccion.current) return
        setTraduccion({ estado: 'error', mensaje: error.message })
      })
  }

  function alTocar(e: React.MouseEvent<HTMLDivElement>) {
    // Un pellizco, o arrastrar la pagina ya ampliada, terminan soltando un
    // `click` igual que un toque cualquiera. Sin esta guardia, ampliar
    // pasaba de pagina o escondia los controles a la vez.
    if (zoom.consumirGesto()) return
    // Traduciendo, el toque no pasa pagina ni esconde los controles: lo unico
    // que hace es elegir el texto. Salir es tan facil como volver a darle al
    // boton, que se queda a la vista mientras el modo esta puesto.
    if (traduciendo) {
      traducirToque(e)
      return
    }
    // El toque contesta al aviso igual que las flechas: en la franja de
    // retroceder confirma, en cualquier otro sitio se queda donde estaba.
    if (preguntaAnterior) {
      let confirma = false
      if (modo === 'paginado') {
        const caja = e.currentTarget.getBoundingClientRect()
        const relativo = (e.clientX - caja.left) / caja.width
        confirma = sentido === 'rtl' ? relativo >= 1 - ZONA_PASAR : relativo <= ZONA_PASAR
      }
      responderAnterior(confirma)
      return
    }
    if (modo === 'webtoon') {
      setVerControles((v) => !v)
      return
    }
    const caja = e.currentTarget.getBoundingClientRect()
    const relativo = (e.clientX - caja.left) / caja.width

    if (relativo > ZONA_PASAR && relativo < 1 - ZONA_PASAR) {
      setVerControles((v) => !v)
      return
    }
    const haciaDelante =
      sentido === 'rtl' ? relativo <= ZONA_PASAR : relativo >= 1 - ZONA_PASAR
    // Una doble se pasa de una vez: sus dos mitades son una sola pagina
    irA(haciaDelante ? siguienteUnidad(indice, dobles) : anteriorUnidad(indice, dobles))
  }

  function cambiarModo() {
    const nuevo: Modo = modo === 'paginado' ? 'webtoon' : 'paginado'
    setModo(nuevo)
    // Al elegirlo a mano se respeta para siempre en esta serie
    if (capitulo?.mangaId) guardarFormato(capitulo.mangaId, nuevo, true)
  }

  /*
   * En webtoon la pagina actual se deduce de lo que se ve en pantalla.
   *
   * Mientras se esta recolocando al abrir no se hace caso: lo que cruza la
   * pantalla no es lectura. Sin esto, al entrar se veia la primera pagina, el
   * indice caia a 0 y a los 1,2 s se guardaba ese 0 como progreso, borrando
   * por donde ibas. Es lo que hacia que un manhwa abriera siempre por el
   * principio: los 21 capitulos empezados de Caballero tenian lastPageRead=0,
   * mientras que en los manga paginados el progreso se guardaba bien.
   */
  useEffect(() => {
    const contenedor = contenedorRef.current
    if (modo !== 'webtoon' || !contenedor) return
    if (porRestaurar !== null) return
    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (entrada.isIntersecting) {
            const n = Number((entrada.target as HTMLElement).dataset.indice)
            if (Number.isFinite(n)) setIndice(n)
          }
        }
      },
      /*
       * La pagina actual es la que cruza una banda fina cerca de lo alto de
       * la pantalla, no la que se vea "en un 40%".
       *
       * Ese `threshold: 0.4` era EL fallo de que los manhwa no guardaran por
       * donde ibas: una pagina de Caballero mide 800x4666, o sea 2275 px de
       * alto a lo ancho de un iPhone, contra una ventana de 844. El maximo
       * que se puede llegar a ver de ella es el 37%, asi que el umbral del
       * 40% no se alcanzaba NUNCA y el observador no disparaba ni una vez:
       * el indice se quedaba clavado en 0 y era ese 0 el que se guardaba.
       * Medido en el banco de pruebas con esas medidas reales: con el umbral,
       * 0 disparos; con la banda, 7 y detectando bien la pagina 6.
       *
       * La banda va al 25% y es de un 1% de alto: casi siempre la cruza una
       * sola pagina, y funciona igual midan lo que midan.
       */
      { root: contenedor.parentElement, rootMargin: '-25% 0px -74% 0px', threshold: 0 },
    )
    for (const hijo of contenedor.children) observador.observe(hijo)
    return () => observador.disconnect()
  }, [modo, paginas, porRestaurar])

  /*
   * Apunta cuanto llevas dentro de la pagina, no solo en que pagina estas.
   *
   * En este manhwa una pagina son ocho pantallas, asi que el numero de pagina
   * por si solo no dice casi nada: hay que guardar tambien lo que llevas
   * recorrido de ella. Solo va al aparato (localStorage); al servidor sigue
   * yendo el numero de pagina, que es lo unico que sabe guardar.
   */
  useEffect(() => {
    if (modo !== 'webtoon' || porRestaurar !== null) return
    const contenedor = contenedorRef.current
    const caja = contenedor?.parentElement
    if (!contenedor || !caja) return

    let temporizador = 0
    function apuntar() {
      const hijo = contenedor!.children[indice] as HTMLElement | undefined
      if (!hijo) return
      const desplazamiento =
        caja!.getBoundingClientRect().top - hijo.getBoundingClientRect().top
      // Se guarda **sin el zoom**: con la tira ampliada al doble, los mismos
      // pixeles son la mitad de lectura, y al reabrir —que siempre es sin
      // zoom— la posicion caeria al doble de lejos.
      guardarPosicion(capituloId, {
        indice,
        desplazamiento: Math.max(0, desplazamiento) / escalaTira,
      })
    }
    function alDesplazar() {
      clearTimeout(temporizador)
      temporizador = window.setTimeout(apuntar, 400)
    }

    caja.addEventListener('scroll', alDesplazar, { passive: true })
    return () => {
      caja.removeEventListener('scroll', alDesplazar)
      clearTimeout(temporizador)
    }
  }, [modo, porRestaurar, indice, capituloId, paginas, escalaTira])

  if (error) {
    return (
      <div className="lector">
        <div className="aviso error" style={{ margin: 20 }}>
          <strong>No se pudieron cargar las paginas</strong>
          <p>{(error as Error).message}</p>
          <button className="boton" onClick={() => navegar(-1)}>
            Volver
          </button>
        </div>
      </div>
    )
  }

  if (isLoading || !paginas) {
    // El texto largo no es adorno: hay fuentes que tardan quince segundos en
    // devolver la lista de paginas, y un "Cargando" a secas parecia colgado.
    return (
      <div className="lector centrado">
        <div>
          Buscando las paginas en la fuente…
          <span className="meta">Algunas tardan unos segundos la primera vez</span>
        </div>
      </div>
    )
  }

  if (terminado) {
    return (
      <div className="lector">
        <div className="fin-capitulo">
          <span className="fin-marca">✓</span>
          <h2>Capitulo terminado</h2>
          <p className="fin-nombre">{capitulo?.name}</p>

          {siguiente ? (
            <button
              className="boton"
              onClick={() => navegar(`/leer/${siguiente.id}`, { replace: true })}
            >
              Siguiente · {siguiente.name}
            </button>
          ) : (
            <p className="meta">Es el ultimo capitulo disponible por ahora.</p>
          )}

          <button
            className="boton secundario"
            onClick={() => {
              setTerminado(false)
              setIndice(paginas.length - 1)
            }}
          >
            Volver a la ultima pagina
          </button>

          <button className="enlace" onClick={volver}>
            Ir a la ficha de la serie
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="lector">
      {rescatando && (
        <div className="lector-rescate">
          <strong>A este capitulo le faltan paginas</strong>
          Se descargo mientras la fuente aun lo estaba subiendo. Buscando el
          resto…
        </div>
      )}

      {preguntaAnterior && anterior && (
        <div className="aviso-anterior">
          <div>
            <strong>¿Ir al capitulo anterior?</strong>
            <span className="meta">{anterior.name}</span>
          </div>
          <div className="aviso-anterior-botones">
            <button className="boton" onClick={() => responderAnterior(true)}>
              Si, ir
            </button>
            <button className="boton secundario" onClick={() => responderAnterior(false)}>
              Quedarme
            </button>
          </div>
        </div>
      )}

      {(verControles || traduciendo) && (
        <header className="lector-barra arriba">
          <button className="icono-accion" onClick={volver}>
            ‹
          </button>
          <div className="lector-titulo">
            <strong>{capitulo?.manga.title}</strong>
            <span>{capitulo?.name}</span>
          </div>
          {/* Enfrente de la flecha de salir, que es la otra cosa que se toca
              en esta barra. Encendido cuando el modo esta puesto: cambia lo
              que hace tocar la pagina, asi que tiene que verse de un vistazo
              si esta activo o no. */}
          <button
            className={traduciendo ? 'boton-traducir activo' : 'boton-traducir'}
            aria-label={traduciendo ? 'Dejar de traducir' : 'Traducir tocando el texto'}
            onClick={() => {
              setTraduciendo(!traduciendo)
              setTraduccion(null)
            }}
          >
            文A
          </button>
        </header>
      )}

      {modo === 'paginado' ? (
        <div className="lector-lienzo" ref={setLienzo} onClick={alTocar}>
          {/* El zoom se aplica al envoltorio y no a la imagen: asi el gesto
              sigue funcionando mientras la pagina siguiente aun no ha
              llegado y lo que hay es el hueco de carga. */}
          <div className={ancha ? 'lector-zoom ancha' : 'lector-zoom'} ref={zoomRef}>
            {esPar ? (
              /*
               * Las dos mitades juntas, y **en orden de lectura**: leyendo de
               * derecha a izquierda la pagina actual es la de la DERECHA y la
               * siguiente la de la izquierda, que es como se imprimieron.
               *
               * La colocacion no sale del ajuste de sentido de lectura sino
               * de la medida (`lib/costuras`): se prueban las dos y gana la
               * que encaja. En las once dobles conocidas la buena saca de
               * 0,726 a 0,959 y la otra no pasa de 0,42, asi que no hay duda,
               * y ademas la doble sigue bien puesta aunque se cambie el
               * ajuste.
               */
              <div className="pagina-doble">
                {[indice, indice + 1]
                  .sort((a, b) => (ordenDe(indice, dobles) === 'rtl' ? b - a : a - b))
                  .map((n) => (
                    <Pagina
                      key={`${revision}:${paginas[n]}`}
                      ruta={paginas[n]!}
                      numero={n + 1}
                      prioritario
                      alMedir={n === indice ? medirPagina : undefined}
                    />
                  ))}
              </div>
            ) : (
              <Pagina
                key={`${revision}:${paginas[indice]}`}
                ruta={paginas[indice]!}
                numero={indice + 1}
                prioritario
                alMedir={medirPagina}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="lector-webtoon" ref={setTira} onClick={alTocar}>
          <div className="lector-tira" ref={contenedorRef}>
            {paginas.map((ruta, i) => (
              <div key={`${revision}:${ruta}`} data-indice={i}>
                <Pagina ruta={ruta} numero={i + 1} prioritario={Math.abs(i - indice) <= 1} />
              </div>
            ))}
          </div>

          {/* Al final de la tira, seguir sin volver a la ficha */}
          <div className="fin-tira">
            <span className="fin-marca">✓</span>
            <p className="fin-nombre">Capitulo terminado</p>
            {siguiente ? (
              <button
                className="boton"
                onClick={() => {
                  void terminar()
                  navegar(`/leer/${siguiente.id}`, { replace: true })
                }}
              >
                Siguiente · {siguiente.name}
              </button>
            ) : (
              <p>Es el ultimo capitulo disponible por ahora.</p>
            )}
            <button className="enlace" onClick={volver}>
              Ir a la ficha de la serie
            </button>
          </div>
        </div>
      )}

      {/* Traduciendo, las dos barras se quedan puestas: un toque en la pagina
          ya no las descubre —lo usa la traduccion— y sin ellas no habria por
          donde salir del modo ni donde leer lo traducido. */}
      {(verControles || traduciendo) && (
        <footer className="lector-barra abajo">
          {traduciendo && (
            <div className="traduccion">
              {traduccion === null && (
                <p className="traduccion-guia">Toca una frase de la pagina</p>
              )}
              {traduccion?.estado === 'pensando' && (
                <p className="traduccion-guia">
                  <span className="girando">↻</span> Leyendo el texto…
                </p>
              )}
              {traduccion?.estado === 'error' && (
                <p className="traduccion-guia error">{traduccion.mensaje}</p>
              )}
              {traduccion?.estado === 'listo' &&
                (traduccion.traduccion ? (
                  <>
                    <p className="traduccion-texto">{traduccion.traduccion}</p>
                    {/* El original debajo y mas apagado: cuando el OCR se
                        equivoca, verlo es lo unico que lo explica. */}
                    <p className="traduccion-original">{traduccion.original}</p>
                  </>
                ) : (
                  <>
                    <p className="traduccion-guia">
                      {traduccion.nota ?? 'No he encontrado texto ahi'}
                    </p>
                    {/* Si el texto se leyo y lo que fallo fue traducirlo, se
                        ensena tal cual: el bocadillo en ingles vale mucho mas
                        que un aviso a secas. */}
                    {traduccion.original && (
                      <p className="traduccion-original">{traduccion.original}</p>
                    )}
                  </>
                ))}
            </div>
          )}

          <div className="lector-ajustes">
            <button className="chip" onClick={cambiarModo}>
              {modo === 'paginado' ? 'Paginado' : 'Tira vertical'}
            </button>
            {modo === 'paginado' && (
              <button
                className="chip"
                onClick={() => setSentido(sentido === 'rtl' ? 'ltr' : 'rtl')}
              >
                {sentido === 'rtl' ? 'Derecha → izquierda' : 'Izquierda → derecha'}
              </button>
            )}
            {/* Unir la pagina con la siguiente cuando la fuente ha partido
                una doble por la mitad. No se puede adivinar cual lo es —esta
                medido en `lib/dobles`—, asi que lo dice el usuario y queda
                guardado para cuando vuelva. */}
            {modo === 'paginado' && indice + 1 < paginas.length && (
              <button
                className={esPar ? 'chip activo' : 'chip'}
                onClick={() => setDobles(alternarDoble(capituloId, indice, dobles, sentido))}
              >
                {esPar ? 'Separar la doble' : 'Unir con la siguiente'}
              </button>
            )}
            {/* Solo aparece con la pagina ampliada. El pellizco al reves ya
                la devuelve a su sitio, pero con un dedo pillado en la
                pantalla no siempre sale, y conviene una salida a la vista. */}
            {zoom.escala > 1 && (
              <button className="chip activo" onClick={zoom.quitar}>
                {zoom.escala.toFixed(1).replace('.', ',')}× · quitar zoom
              </button>
            )}
            <span className="contador">
              {esPar ? `${indice + 1}-${indice + 2}` : indice + 1} / {paginas.length}
            </span>
          </div>

          {modo === 'paginado' && (
            <input
              type="range"
              min={0}
              max={paginas.length - 1}
              value={indice}
              onChange={(e) => setIndice(inicioUnidad(Number(e.target.value), dobles))}
              // La barra crece hacia el mismo lado al que se avanza
              style={{ direction: sentido === 'rtl' ? 'rtl' : 'ltr' }}
            />
          )}
        </footer>
      )}
    </div>
  )
}
